import React, { useMemo } from 'react'
import { Grid, Card, CardContent, Box, Typography } from '@mui/material'
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk'
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback'
import CallMissedIcon from '@mui/icons-material/CallMissed'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CancelIcon from '@mui/icons-material/Cancel'
import ScheduleIcon from '@mui/icons-material/Schedule'
import { cardStyles } from '../../../helpers/style'
import { operatorReportStyles as opStyles } from '../../../helpers/style'
import { greenTheme } from '../../../helpers/style'
import { reportFromFacets } from './reportFacets'

const primaryColor = greenTheme.primary

const mainCards = [
  { key: 'received', color: primaryColor, Icon: PhoneInTalkIcon, labelKey: 'received', valueKey: 'received' },
  { key: 'answered', color: '#22c55e', Icon: PhoneCallbackIcon, labelKey: 'callAnswered', valueKey: 'answered' },
  { key: 'notAnswered', color: '#ef4444', Icon: CallMissedIcon, labelKey: 'callNotAnswered', valueKey: 'notAnswered' },
  { key: 'totalDuration', color: '#6366f1', Icon: AccessTimeIcon, labelKey: 'totalDuration', valueKey: 'totalDuration', format: 'duration' },
]

const smallCards = [
  { key: 'answeredLater', color: '#6ee7b7', Icon: ScheduleIcon, labelKey: 'callAnsweredLater', valueKey: 'answeredLater' },
  { key: 'busy', color: '#f59e0b', Icon: PhoneDisabledIcon, labelKey: 'callBusy', valueKey: 'busy' },
  { key: 'failed', color: '#dc2626', Icon: ErrorOutlineIcon, labelKey: 'callFailed', valueKey: 'failed' },
  { key: 'canceled', color: '#6b7280', Icon: CancelIcon, labelKey: 'callCanceled', valueKey: 'canceled' },
]

export default function ReportSummaryCards({
  report,
  opTr,
  langIndex,
  getCallTypeLabel,
  formatDuration,
  historyData,
  historyFacetFields,
}) {
  const facetFields = historyFacetFields ?? historyData?.facet_counts?.facet_fields ?? null
  const displayReport = useMemo(
    () => reportFromFacets(report, facetFields),
    [report, facetFields]
  )

  const formatDurationOrHours = (seconds) => {
    const total = Math.floor(Number(seconds) || 0)
    if (total >= 3600) {
      const h = Math.floor(total / 3600)
      const m = Math.floor((total % 3600) / 60)
      const s = total % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    }
    return formatDuration(seconds)
  }

  return (
    <>
      <Box id="section-call-count" sx={opStyles.sectionBox}>
        <Grid container spacing={3} sx={opStyles.gridMb}>
          {mainCards.map(({ key, color, Icon, labelKey, valueKey, format }) => (
            <Grid item xs={12} sm={6} md={3} key={key} sx={{ display: 'flex' }}>
              <Card sx={{ ...cardStyles.card, borderLeft: `4px solid ${color}`, minHeight: 120, height: '100%', flex: 1 }}>
                <CardContent sx={cardStyles.cardContent}>
                  <Box sx={opStyles.cardIconBox}>
                    <Icon sx={{ color, fontSize: key === 'received' ? undefined : 28, ...(key === 'received' && opStyles.performanceIcon) }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      {key === 'answered' ? getCallTypeLabel('answered') : key === 'notAnswered' ? getCallTypeLabel('not_answered') : opTr[labelKey]?.[langIndex]}
                    </Typography>
                  </Box>
                  <Typography variant="h4" sx={{ ...opStyles.cardValueTypography, color }}>
                    {format === 'duration' ? formatDurationOrHours(displayReport[valueKey]) : displayReport[valueKey]}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
      <Grid container spacing={2} sx={opStyles.gridMb}>
        {smallCards.map(({ key, color, Icon, labelKey, valueKey }) => (
          <Grid item xs={12} sm={6} md={3} key={key} sx={{ display: 'flex' }}>
            <Card sx={{ ...cardStyles.card, borderLeft: `4px solid ${color}`, minHeight: 80, height: '100%', flex: 1 }}>
              <CardContent sx={{ ...cardStyles.cardContent, ...opStyles.smallCardContent }}>
                  <Box sx={opStyles.smallCardInnerBox}>
                  <Box sx={opStyles.smallCardLeftBox}>
                    <Icon sx={{ color, fontSize: 24 }} />
                    <Typography variant="subtitle2" color="text.secondary">
                      {opTr[labelKey]?.[langIndex] ?? getCallTypeLabel(valueKey)}
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ ...opStyles.cardValueTypography, color }}>
                    {displayReport[valueKey]}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  )
}
